// Which puzzle a Play or next-game button opens, for this player (CLAUDE.md:
// Every Play and next-game button picks the player's own next puzzle).
//
// One rule for every game, worked out on this device from two things: which
// puzzles are released (`archive_list`, the server's) and which this device has
// finished (its own record). **A replay is never a finish**: it is neither
// recorded nor marked, so it changes nothing here.
//
// - Running Order: today's daily if this device has not finished it; otherwise
//   the oldest released daily it has not finished.
// - Call Sheet, and any pool game added later: the oldest released puzzle it
//   has not finished.
// - Nothing left: "done", and the button becomes the countdown to the moment
//   the next one actually appears (`next_arrivals`, the server's). Where nothing
//   is coming, or that moment has passed and the puzzle is not there yet, the
//   countdown shows nothing; the choice is asked again at the moment, and every
//   half-minute after it, until the puzzle is there.
//
// **The answer is known from the device whenever it can be** (owner, 28
// October). What is released only changes at UK midnight, so the listing,
// today's daily number and the next arrival are kept on the device with the UK
// day they were read on, and a button drawn on the same day is decided from
// them at once — no wait for the server, nothing to flicker. The server is
// still asked every time, and its answer replaces the device's if they differ
// (a withdrawal in the day, say). A new day, or a device with nothing kept,
// waits for the server as before.
import { useEffect, useState } from 'react';
import { ukToday } from './dates.ts';
import { archiveList, type ArchiveListing } from './archive.ts';
import { rpc } from './api.ts';
import { outcomes, type GameKey } from './history.ts';
import { playedNumbers } from '../games/call-sheet/played.ts';
import { forgetToday, todaysNumber } from '../games/running-order/today.ts';

/** A puzzle to open, every released one finished, or nothing released at all. */
export type NextPick = { number: number } | 'done' | null;

// What the device keeps, each with the UK day it was read on; read back only
// on that same day. A `cinemore-` key, so Reset and Delete my results clear it.
const KEPT = 'cinemore-next-cache';
interface Kept {
  day: string;
  list?: ArchiveListing;
  today?: number | null;
  arrival?: Partial<Record<GameKey, number>>;
}
function kept(): Kept | null {
  try {
    const k = JSON.parse(localStorage.getItem(KEPT) ?? 'null') as Kept | null;
    return k && k.day === ukToday() ? k : null;
  } catch { return null; }
}
function keep(change: Omit<Kept, 'day'>): void {
  try {
    const day = ukToday();
    const was = kept() ?? { day };
    const arrival = change.arrival ? { ...was.arrival, ...change.arrival } : was.arrival;
    localStorage.setItem(KEPT, JSON.stringify({ ...was, ...change, arrival, day }));
  } catch { /* nothing kept: the server is asked, as before */ }
}

// One listing per moment, however many buttons ask for it on a page.
let listing: { at: number; list: Promise<ArchiveListing> } | null = null;
function released(): Promise<ArchiveListing> {
  if (!listing || Date.now() - listing.at > 10_000) {
    const list = archiveList();
    listing = { at: Date.now(), list };
    list.then((l) => keep({ list: l }), () => { listing = null; });
  }
  return listing.list;
}

/** The pick, worked out from a listing and today's number. */
function pickFrom(game: GameKey, list: ArchiveListing, today: number | null, done: Set<number>): NextPick {
  if (game === 'running-order' && today !== null && !done.has(today)) return { number: today };
  const numbers = game === 'running-order' ? list.runningOrder : list.callSheet;
  if (numbers.length === 0) return null;
  let oldest: number | null = null;
  for (const n of numbers) if (!done.has(n) && (oldest === null || n < oldest)) oldest = n;
  return oldest === null ? 'done' : { number: oldest };
}

/** The answer from what this device kept today, or undefined where it cannot
 *  be decided without the server. */
function keptState(game: GameKey, exclude?: number): NextState | undefined {
  const k = kept();
  if (!k?.list || k.today === undefined) return undefined;
  const done = finished(game);
  if (exclude !== undefined) done.add(exclude);
  const pick = pickFrom(game, k.list, k.today, done);
  if (pick !== 'done') return { pick, arrival: null };
  const at = k.arrival?.[game];
  return at !== undefined && at > Date.now() ? { pick, arrival: at } : undefined;
}

function finished(game: GameKey): Set<number> {
  const done = new Set(outcomes(game).keys());
  // Call Sheet keeps a second record of first finishes (played.ts); either says finished.
  if (game === 'call-sheet') for (const n of playedNumbers()) done.add(n);
  return done;
}

/**
 * The puzzle this device should play next in `game`. `exclude` is a puzzle to
 * treat as finished — the one on screen, whose finish a result screen may ask
 * about a moment before it has been written down.
 */
export async function pickNext(game: GameKey, exclude?: number): Promise<NextPick> {
  const done = finished(game);
  if (exclude !== undefined) done.add(exclude);
  // Both asked at once; today's daily alone settles Running Order when it is
  // unfinished, so a failed listing does not cost that answer.
  const [today, list] = await Promise.all([todaysNumber(), released().catch(() => null)]);
  if (list === null) {
    if (game === 'running-order' && today !== null && !done.has(today)) return { number: today };
    throw new Error('The archive listing could not be read.');
  }
  // Kept only alongside a listing that arrived: a failed request reads as "no
  // daily today", which is not a thing to remember.
  keep({ today });
  return pickFrom(game, list, today, done);
}

interface Arrivals { runningOrder: string | null; callSheet: string | null }

/** When the next puzzle of `game` will appear, as a time in ms, or null where
 *  nothing is coming. Asked fresh: it is only wanted once everything is done. */
export async function nextArrival(game: GameKey): Promise<number | null> {
  const a = await rpc<Arrivals>('next_arrivals', {});
  const at = game === 'running-order' ? a.runningOrder : a.callSheet;
  const ms = at ? Date.parse(at) : null;
  if (ms !== null) keep({ arrival: { [game]: ms } });
  return ms;
}

/** A pick, and with "done" the moment the next puzzle appears (null: none coming). */
export interface NextState { pick: NextPick; arrival: number | null }

/** `undefined` while it is being worked out. */
export function useNextPuzzle(game: GameKey, exclude?: number): NextState | undefined {
  // From the device at once where it can say; the server confirms below.
  const [state, setState] = useState<NextState | undefined>(() => keptState(game, exclude));
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    let live = true;
    (async () => {
      const pick = await pickNext(game, exclude);
      const arrival = pick === 'done' ? await nextArrival(game).catch(() => null) : null;
      // Only a real difference is drawn, so the server agreeing changes nothing.
      if (live) setState((was) => same(was, { pick, arrival }) ? was : { pick, arrival });
    })().catch(() => { if (live) setState((was) => was ?? { pick: null, arrival: null }); });
    return () => { live = false; };
  }, [game, exclude, asked]);

  // Everything finished: ask again a second after the next puzzle is due, then
  // every half-minute until it is there; with no time known, every
  // minute. Each ask forgets the cached listing and today's number, which are
  // exactly what has changed at midnight.
  useEffect(() => {
    if (state?.pick !== 'done') return;
    const left = state.arrival === null ? null : state.arrival - Date.now();
    const wait = left === null ? 60_000 : left > 0 ? left + 1_000 : 30_000;
    const timer = setTimeout(() => {
      listing = null; forgetToday();
      try { localStorage.removeItem(KEPT); } catch { /* nothing kept */ }
      setAsked((n) => n + 1);
    }, wait);
    return () => clearTimeout(timer);
  }, [state]);

  return state;
}

function same(a: NextState | undefined, b: NextState): boolean {
  if (!a || a.arrival !== b.arrival) return false;
  if (a.pick === b.pick) return true;
  return typeof a.pick === 'object' && a.pick !== null && typeof b.pick === 'object' && b.pick !== null
    && a.pick.number === b.pick.number;
}

/** When the next puzzle of `game` appears, for a countdown that is not standing
 *  in for a button (Running Order's under its result screen's buttons). */
export function useArrival(game: GameKey): number | null | undefined {
  const [at, setAt] = useState<number | null | undefined>(undefined);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    let live = true;
    nextArrival(game).then((t) => { if (live) setAt(t); }, () => { if (live) setAt(null); });
    return () => { live = false; };
  }, [game, asked]);
  useEffect(() => {
    if (at === undefined) return;
    const left = at === null ? null : at - Date.now();
    const wait = left === null ? 60_000 : left > 0 ? left + 1_000 : 30_000;
    const timer = setTimeout(() => setAsked((n) => n + 1), wait);
    return () => clearTimeout(timer);
  }, [at]);
  return at;
}
